'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProductRecommendations } from '@org/ui';
import ProductCard from '@/components/ProductCard';
import ProductRecommendationsSkeleton from './ProductRecommendationsSkeleton';

const RECOMMENDATIONS_LIMIT = 8;

function ProductRecommendationsSection({ detailData }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const recommendationSections = Array.isArray(detailData?.recommendationSections) 
    ? detailData.recommendationSections 
    : [];

  const recommendationSectionsKey = useMemo(
    () => recommendationSections.join(','),
    [recommendationSections]
  );

  useEffect(() => {
    const controller = new AbortController();

    const loadRecommendations = async () => {
      if (
        recommendationSections.length === 0 &&
        !detailData?.brand &&
        !detailData?.code
      ) {
        setItems([]);
        setLoading(false);
        setError(false);
        return;
      }

      setItems([]);
      setLoading(true);
      setError(false);

      try {
        const params = new URLSearchParams();
        if (recommendationSections.length > 0) {
          params.set('sectionCode', recommendationSections.join(','));
        }
        if (detailData?.brand) {
          params.set('brand', detailData.brand);
        }
        if (detailData?.code) {
          params.set('excludeCode', detailData.code);
        }
        params.set('limit', String(RECOMMENDATIONS_LIMIT));

        const response = await fetch(`/api/recommendations?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load recommendations: ${response.status}`);
        }

        const payload = await response.json();
        if (!controller.signal.aborted) {
          const received = Array.isArray(payload?.items) ? payload.items : [];
          setItems(received);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('[ProductRecommendationsSection] failed', err);
          setItems([]);
          setError(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadRecommendations();

    return () => controller.abort();
  }, [detailData?.brand, detailData?.code, recommendationSectionsKey, recommendationSections]);

  const renderItem = useCallback(
    (item, index) => (
      <ProductCard
        key={item?.code || item?.id || `recommended-${index}`}
        item={item}
        className="h-full"
      />
    ),
    []
  );

  if (loading && items.length === 0) {
    return <ProductRecommendationsSkeleton />;
  }

  return (
    <ProductRecommendations
      items={items}
      loading={loading}
      error={error}
      renderItem={renderItem}
    />
  );
}

export default ProductRecommendationsSection;
